import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Table, Text,
    FileInput,
    Progress,
} from '@mantine/core';
import { RefinedSubTrade } from 'app/models/traded-facilities-params';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { signableDocumentTypeTagOptions, signedByDocumentTrade, tradeDocumentTag } from 'app/models/dropdown-options';
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput';
import { useForm } from '@mantine/form'
import IonIcon from '@reacticons/ionicons';
import axios from 'axios'
import { createDocument, deleteDocument, generatePresignedUrl, getDocumentFromS3 } from 'app/state/ducks/documents/thunks';
import { formatDateTimeToShow, formatDateToUTC, highlightToday, stringToDate } from 'app/utils/util-functions';
import { validatePdfUpload } from 'app/utils/pdf-validation';
import { useSelector } from 'react-redux';
import { ContractType, DocumentStateTypes } from 'app/models/common-types';
import { getDocuments } from 'app/state/ducks/documents/selectors';
import { Document, SignableDocument } from 'app/models/document-params'
import { TextLabelInput } from 'app/views/components/inputs/TextLabelInput';
import { DateInput } from '@mantine/dates';
import { getSignableDocuments } from 'app/state/ducks/signable-document/selectors';
import { getMasterTrades } from 'app/state/ducks/master-trades/selectors';
import DocumentPreviewModal from './document-preview-modal';
import { getSubTradeSignableDocument, getMergedDocument, loadFMMetadataForSubTrade } from 'app/state/ducks/signable-document/thunks';
import { getPossibleActions, onSignDocument } from '../../utils/signable-document-utils';
import moment from 'moment';
import { useUnappliedTransactions } from 'app/views/pages/trade-entry/utils/useUnappliedTransactions';
import UnappliedTransactionWarningModal from 'app/views/components/unapplied-transaction-warning/UnappliedTransactionWarningModal';
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh';

interface DetailPanelProps {
    refinedSubTrade: RefinedSubTrade;
}

const STAGE_LIMITS = {
    presignedMax: 20,
    uploadMax: 70,
    createLedgerRefernceMax: 80,
    scannedMax: 100,
};

export function DocumentTradePanel({ refinedSubTrade }: DetailPanelProps) {
    const dispatch = useDispatch();
    const { refreshDocuments } = useTradeRefresh();
    const navigate = useNavigate();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteDocuments, setDeleteDocuments] = useState<Document>({} as Document);
    const [showUpload, setShowUpload] = useState(false);
    const [progressValue, setProgressValue] = useState(0)
    const [uploadInProgress, setUploadInProgress] = useState(false)
    const [openPreviewDocument, setOpenPreviewDocument] = useState<boolean>(false);
    const [signableDocument, setSignableDocument] = useState<SignableDocument | null>(null);
    const [currentDocumentType, setCurrentDocumentType] = useState<string>('');
    const [showUnappliedWarning, setShowUnappliedWarning] = useState(false);
    const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
    const activeSessionRef = useRef<string | null>(null);
    const allAttachments = useSelector(getDocuments)
    const allSignableDocuments = useSelector(getSignableDocuments)
    const allMasterTrades = useSelector(getMasterTrades)
    const redirectToSubTrade = () => {
        const masterTradeId = refinedSubTrade.masterTradeId?.id;
        if (masterTradeId) {
            navigate(`/tradeentry/${masterTradeId}/subtrade`);
        }
    };


    const signableDocuments = useMemo(() => {
        return allSignableDocuments.filter(doc => doc.contractId === refinedSubTrade.id && doc.contractType === ContractType.SubTrades)
    }, [allSignableDocuments, refinedSubTrade.id])

    const attachementsMasterTrade = useMemo(() => {
        const documentStateType = DocumentStateTypes[ContractType.MasterTrade]
        return allAttachments[documentStateType]?.filter(atm => atm.contractId.id === refinedSubTrade.id) ?? []

    }, [allAttachments, refinedSubTrade.id])

    const masterTrade = useMemo(() => {
        const found = allMasterTrades.find(mt => mt.id === refinedSubTrade.masterTradeId.id) ?? null;
        return found;
    }, [allMasterTrades, refinedSubTrade.masterTradeId]);

    const hasUnappliedTransactions = useUnappliedTransactions(masterTrade?.id);




    const allDocuments = useMemo(() => {
        const signableDocumentTableData = signableDocuments.map(doc => {
            // For LendOS-generated documents, show only actual DocuSeal signatures
            // Do NOT include adminSignedFor - those are references to separately uploaded documents
            const allSigners: string[] = [];

            // Add signatures from DocuSeal/online signatures only
            if (Array.isArray(doc.signatures) && doc.signatures.length > 0) {
                doc.signatures.forEach(sig => {
                    if (sig && typeof sig === 'object' && sig.signerType) {
                        allSigners.push(sig.signerType);
                    }
                });
            }

            // Remove duplicates and join with ' & '
            const uniqueSigners = [...new Set(allSigners)];

            return {
                name: doc.fileName ?? "Unknown Document.pdf",
                tag: signableDocumentTypeTagOptions.find(({ value }) => value === doc.documentType)?.label ?? "",
                signedBy: uniqueSigners.length > 0 ? uniqueSigners.join(' & ') : '',
                source: "LendOS",
                date: formatDateTimeToShow(new Date(doc.createDate)),
                onPreview: () => handleOpenSignableDocument(refinedSubTrade.id ?? '', doc.documentType),
                origin: doc,
            };
        });
        const attachmentDocumentTableData = attachementsMasterTrade.map(doc => ({
            name: doc.name,
            tag: doc.tags[0] ?? "",
            signedBy: signedByDocumentTrade.find(option => option.value === doc.signedBy)?.label ?? '',
            source: "Attached",
            date: formatDateTimeToShow(new Date(doc.createDate)),
            onPreview: () => handleOpen(doc),
            origin: doc,
        }));
        return [...signableDocumentTableData, ...attachmentDocumentTableData].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [signableDocuments, attachementsMasterTrade, masterTrade]);

    const form: any = useForm({
        initialValues: {
            id: undefined,
            name: '',
            mimeType: '',
            requestor: '',
            tags: '',
            signedBy: '',
            file: null,
            effectiveDate: stringToDate(
                moment(refinedSubTrade.targetSettlementDate).format('YYYY-MM-DD'),
            ),
        },
        validate: {
            file: (value) => (value ? null : 'File is required'),
            tags: (value) => (value ? null : 'Type is required'),
            signedBy: (value) => (value ? null : 'Actioned By is required'),
            effectiveDate: (value, values) => {
                const needsDate =
                    values.tags === 'AA' &&
                    ['AgentDocumentTrade', 'SellerAndBuyerAndAgentDocumentTrade'].includes(form.values.signedBy);
                if (needsDate && !value) {
                    return 'Settlement date is required when actioned by Agent';
                }
                return null;
            },
        }
    });

    // Filter signedBy options based on document type
    // C (Confirmation) and FM (Funding Memo) documents are only signed by Seller and/or Buyer
    // Agent never signs these document types

    const filteredSignedByOptions = useMemo(() => {
        const documentType = form.values.tags;

        if (documentType === 'C' || documentType === 'FM') {
            return signedByDocumentTrade.filter(option =>
                !option.value.includes('Agent')
            );
        }

        return signedByDocumentTrade;
    }, [form.values.tags]);

    useEffect(() => {
        const documentType = form.values.tags;
        if ((documentType === 'C' || documentType === 'FM') &&
            form.values.signedBy &&
            form.values.signedBy.includes('Agent')) {
            form.setFieldValue('signedBy', '');
        }
    }, [form.values.tags, form.values.signedBy, form]);


    const possibleActions = useMemo(() => {
        return getPossibleActions(masterTrade, signableDocument);
    }
        , [masterTrade, signableDocument]);



    const handleDelete = async (row: any) => {
        try {
            const result: any = await dispatch(deleteDocument(row.id))
            if (result && result.success) {
                SuccessNotification({ title: 'Document succesfuly deleted' });
                return
            } else {
                ErrorNotification({
                    title: 'Document failed to delete',
                    message: result?.payload ?? "Check data and try again"
                });
            }
        } catch (error) {
            console.error('Error deleting document:', error);
            ErrorNotification({
                title: 'Document failed to delete',
                message: 'Check data and try again',
            });
        } finally {
            setShowDeleteModal(false);
        }
    };

    const uploadForm = async (url: string, file: File) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            await axios.put(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round(STAGE_LIMITS.presignedMax + (progressEvent.progress ?? 0.0) * (STAGE_LIMITS.uploadMax - STAGE_LIMITS.presignedMax));
                    setProgressValue(progress);
                },
            });
            return { success: true, payload: file };
        } catch (error: any) {
            console.error("Error uploading file to S3:", error);
            return { success: false, payload: error?.message };

        }
    };

    const setFile = async (file: File | null) => {
        if (form.validate().hasErrors) return
        if (!file) return

        if (hasUnappliedTransactions) {
            setPendingUploadFile(file);
            setShowUnappliedWarning(true);
            return;
        }

        await performUpload(file);
    };

    const performUpload = async (file: File) => {
        // Password-protected PDFs break packet/EML merging downstream (T-002942);
        // reject them before uploading (see app/utils/pdf-validation.ts).
        const pdfValidation = await validatePdfUpload(file)
        if (pdfValidation !== 'ok') {
            ErrorNotification({
                title: 'Document failed to upload',
                message: pdfValidation === 'password-protected'
                    ? 'This PDF is password-protected. Re-save it without protection (e.g. print to PDF) and upload again.'
                    : 'The selected file could not be read. It may have been moved or changed since it was selected — choose the file again.',
            })
            return
        }

        const contractKey = {
            id: refinedSubTrade.id,
            admin: refinedSubTrade.agencyAdmin,
        }

        setUploadInProgress(true)
        setProgressValue(1);
        let presignProgress = 0;
        const presignInterval = setInterval(() => {
            presignProgress += 1;
            if (presignProgress <= STAGE_LIMITS.presignedMax - 1) {
                setProgressValue(presignProgress);
            }
        }, 50); // Adjust for desired speed

        const presignedResponse = await generatePresignedUrl(file.name, file.type)
        if (!presignedResponse || !presignedResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: presignedResponse?.payload ?? "Check data and try again"
            });

            setUploadInProgress(false)
            return
        }
        clearInterval(presignInterval)
        setProgressValue(STAGE_LIMITS.presignedMax)
        const { url, id } = presignedResponse.payload
        const presignedId = id
        const uploadResponse: any = await uploadForm(url, file)
        //THIS IS FOR AA DOCUMENTS IN Signing Portal
        if (form.values.tags === 'AA') {
            const presignedSPResponse = await generatePresignedUrl(file.name, file.type, 'signing', presignedId)
            const { url } = presignedSPResponse.payload
            await uploadForm(url, file)
        }
        if (!uploadResponse || !uploadResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: uploadResponse?.payload ?? "Check data and try again"
            });
            setUploadInProgress(false)
            return
        }
        setProgressValue(STAGE_LIMITS.uploadMax);
        let creationProgress = STAGE_LIMITS.uploadMax;
        const creationInterval = setInterval(() => {
            creationProgress += 1;
            if (creationProgress <= STAGE_LIMITS.createLedgerRefernceMax - 1) {
                setProgressValue(creationProgress);
            }
        }, 50); // Adjust for desired speed
        const settlementDateUtc: string | null =
            form.values.tags === 'AA' && ['AgentDocumentTrade', 'SellerAndBuyerAndAgentDocumentTrade'].includes(form.values.signedBy)
                ? formatDateToUTC(form.values.effectiveDate)
                : null;

        const newDocument = {
            id,
            fileName: file.name,
            fileType: file.type,
            contractId: contractKey,
            contractType: 'SubTrades',
            tags: form.values.tags,
            signedBy: form.values.signedBy,
            masterTradeId: refinedSubTrade.masterTradeId,
            effectiveDate: settlementDateUtc,
            documentType: form.values.tags,
            documentTypeText: form.values.tags === 'AA' ? null : form.values.tags === 'FM' ? null : form.values.tags === 'C' ? null : 'Trade Document',
        }

        const damlResponse: any = await dispatch(createDocument(newDocument))
        if (!damlResponse || !damlResponse.success) {
            ErrorNotification({
                title: 'Document failed to upload',
                message: damlResponse?.payload ?? "Check data and try again"
            });
            setUploadInProgress(false)
            return
        }
        clearInterval(creationInterval)
        setProgressValue(STAGE_LIMITS.createLedgerRefernceMax)


        let scanningProgress = STAGE_LIMITS.createLedgerRefernceMax;
        const scanningInterval = setInterval(() => {
            scanningProgress += 1;
            if (scanningProgress <= STAGE_LIMITS.scannedMax - 1
            ) {
                setProgressValue(scanningProgress);
            }
        }, 50); // Adjust for desired speed
        await new Promise(r => setTimeout(r, 1500)); // simulate delay
        clearInterval(scanningInterval)
        setProgressValue(STAGE_LIMITS.scannedMax)
        setUploadInProgress(false)
        SuccessNotification({ title: 'Document successfully uploaded' });
        form.reset();
        // Uploading an offline-signed document records the signature on the
        // backend; an agent-signed AA upload also settles the sub-trade, which
        // auto-locks the Funding Memo. Re-read the tab (one request: the
        // trade, its sub trades, documents and funding-memo lock state) so the
        // signed icon, the row and the lock icon update without a manual page
        // refresh (ADR_LOS-030 step 3).
        refreshDocuments()
        if (settlementDateUtc && refinedSubTrade.id) {
            dispatch(loadFMMetadataForSubTrade(refinedSubTrade.id))
        }
    }

    const handleWarningConfirm = async () => {
        if (pendingUploadFile) {
            await performUpload(pendingUploadFile);
        }
        setShowUnappliedWarning(false);
        setPendingUploadFile(null);
    };

    const handleWarningCancel = () => {
        setShowUnappliedWarning(false);
        setPendingUploadFile(null);
    };

    const handleOpenSignableDocument = async (tradeId: string, documentType: string) => {
        const session = `${tradeId}:${documentType}`;
        activeSessionRef.current = session;
        const isAA = documentType === 'AssignmentAgreementDocument';
        const response: any = isAA
            ? await getMergedDocument(tradeId, documentType)
            : await getSubTradeSignableDocument(tradeId, documentType);

        if (activeSessionRef.current !== session) return;

        if (!response || !response.success) {
            ErrorNotification({
                title: 'Error',
                message: response?.payload || `Failed to retrieve the ${isAA ? 'merged ' : ''}document.`,
            })
            return;
        }

        setSignableDocument(response.payload);
        setCurrentDocumentType(documentType);
        setOpenPreviewDocument(true);
    }

    const handleSignDocument = async (signBy: "buyer" | "seller" | "both") => {
        const session = activeSessionRef.current;
        const signed = await onSignDocument(signBy, signableDocument, dispatch);
        if (activeSessionRef.current !== session) return;
        if (signed) setSignableDocument(signed);
    };

    // Added temporarily to allow viewing trade confirmation docs in browser instead of using modal
    // until a solution is found for displaying the document in the modal (DocumentPreviewModal)
    // This is a workaround to open the document in a new tab directly
    const handleDocumentNameClick = async (doc: any) => {
        if (doc.documentType !== "ConfirmationDocument") {
            return;
        }
        // If it's a signable document, fetch the full document to get the URL
        if (doc.documentType && doc.contractId) {
            // Fetch the signable document (returns { payload: { url: ... } })
            const signableDocumentResponse: any = await getSubTradeSignableDocument(doc.contractId, doc.documentType);
            if (signableDocumentResponse && signableDocumentResponse.success && signableDocumentResponse.payload?.url) {
                window.open(signableDocumentResponse.payload.url, '_blank');
                return;
            }
        }
        // Otherwise, try to fetch from S3 (for attached docs)
        if (doc.id) {
            const downloadResponse = await getDocumentFromS3(doc.id);
            if (!downloadResponse || !downloadResponse.success) {
                ErrorNotification({
                    title: 'Document failed to download',
                    message: downloadResponse?.payload ?? "Check data and try again"
                });
                return;
            }
            const { url } = downloadResponse.payload;
            window.open(url, '_blank');
            return;
        }
        ErrorNotification({
            title: 'Document failed to open',
            message: 'Document does not have a valid URL or ID.'
        });
    };


    const handleOpen = async (row: any) => {
        const downloadResponse = await getDocumentFromS3(row.id)
        if (!downloadResponse || !downloadResponse.success) {
            ErrorNotification({
                title: 'Document failed to download',
                message: downloadResponse?.payload ?? "Check data and try again"
            });
            return
        }
        const { url } = downloadResponse.payload
        window.open(url, '_blank')
    };

    return (
        <div className='document-details'>
            <div className='document-overview'>
                {showUpload && (
                    <div className="document-overview-upload">
                        <div className='document-overview-upload-inside'>
                            <div
                            >
                                <FileInput
                                    classNames={{
                                        rightSection: "customSellectRightSection",
                                        input: "customSelectInput",
                                        wrapper: "newInputWrapper",
                                    }}
                                    rightSection={<IonIcon style={{ width: "18px", height: "18px", top: "2px", left: "2px" }} color="black" name="search" />}
                                    accept="application/pdf"
                                    placeholder="Select File..."
                                    {...form.getInputProps('file')}
                                    style={{ width: '450px' }}
                                />
                            </div>
                            <NewSelectInput label="Type" data={tradeDocumentTag} w="100px" placeholder='Type' {...form.getInputProps('tags')} />
                            <NewSelectInput label="Actioned By" fromTradeDocument={true} data={filteredSignedByOptions} w="193px" placeholder='Actioned By' {...form.getInputProps('signedBy')} />
                            {form.values.tags === 'AA' && ['AgentDocumentTrade', 'SellerAndBuyerAndAgentDocumentTrade'].includes(form.values.signedBy) && (
                                <TextLabelInput label="Settlement Date" fromTradeDocument={true}>
                                    <DateInput
                                        w="133px"
                                        placeholder="Date"
                                        valueFormat="DD-MMM-YYYY"
                                        firstDayOfWeek={0}
                                        classNames={{
                                            input: 'newInput',
                                            wrapper: 'newInputWrapper',
                                            rightSection: 'customRightSection',
                                        }}
                                        rightSection={
                                            <IonIcon size="large" name="chevron-down-outline" />
                                        }
                                        getDayProps={highlightToday()}
                                        {...form.getInputProps('effectiveDate')}
                                    />
                                </TextLabelInput>)}
                            <button
                                type="button"
                                className="document-valivation-header-button-add-document"
                                onClick={() => setFile(form.values.file)}
                            >
                                <div className="document-valivation-header-button-add-document-text">
                                    Upload
                                </div>
                            </button>
                            <IonIcon className='drawerCloseButton' name='close-circle' onClick={() => { setShowUpload(false); form.reset(); }} />
                        </div>
                        {uploadInProgress && <div className='attachments-progress'>
                            <Text className='attachments-progress-header'>Progress ...</Text>
                            <Progress w="100%" value={progressValue} h="30px" label={`${progressValue}%`} size="xl" radius="xl" classNames={{
                                root: "attachments-progress-root",
                                label: "attachments-progress-label",
                            }} />
                        </div>}
                    </div>
                )}
                <div className='document-validation'>
                    <div className='document-validation-header'>
                        <div className='document-validation-header-text'>Documents summary</div>
                        <div className='document-validation-header-button-total'>
                            {(!showUpload && refinedSubTrade.status !== 'Canceled') && (
                                <button
                                    type="button"
                                    className="document-valivation-header-button-add-document"
                                    onClick={() => setShowUpload(true)}
                                >
                                    <div className="document-valivation-header-button-add-document-text">
                                        Add Document
                                    </div>
                                </button>
                            )}
                            <div className='document-validation-header-frame'>
                                <div className='document-validation-header-frame-inside'>
                                    <div className='document-validation-header-frame-inside-total-document'>
                                        <div className='document-validation-header-frame-inside-total-document-text'>Total Documents:
                                        </div>
                                    </div>
                                    <div className='document-validation-header-frame-inside-counter'>
                                        <div className='document-validation-header-frame-inside-counter-text'>
                                            {allDocuments.length}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='document-summary-table'>
                        <div className='document-summary-table-inner'>
                            <Table highlightOnHover striped={false} className="remove-stripe-table " style={{ width: '100% !important' }}>
                                <thead style={{ backgroundColor: '#F0EEEE' }}>
                                    <tr className="document-summury-overview-table-header">
                                        <th className='text-header'>Documents</th>
                                        <th className='text-header'>Type</th>
                                        <th className='text-header'>Signed By</th>
                                        <th className='text-header'>Source</th>
                                        <th className='text-header'>Upload Date</th>
                                        <th className='text-header'></th>
                                        <th className='text-header'></th>
                                    </tr>
                                </thead>
                                <tbody className="table-body">
                                    {allDocuments.map((e, index) => (
                                        <tr key={`${e.name}-${index}`} className="table-row">
                                            <td className="document-summury-overview-table-cell" style={{ textAlign: 'start' }} onClick={() => handleDocumentNameClick(e.origin)}>{e.name ?? ''}</td>
                                            <td className="document-summury-overview-table-cell" style={{ textAlign: 'start' }}>{e.tag ?? ''}</td>
                                            <td className="document-summury-overview-table-cell" style={{ textAlign: 'start' }} >{e.signedBy}</td>
                                            <td className="document-summury-overview-table-cell" style={{ textAlign: 'start' }}>{e.source}</td>
                                            <td className="document-summury-overview-table-cell" style={{ textAlign: 'start' }}>{e.date}</td>
                                            <td className="document-summury-overview-table-cell" style={{ marginLeft: '-10px', cursor: 'pointer' }} onClick={e.onPreview}>View</td>
                                            <td className="document-summury-overview-table-cell" >
                                                {refinedSubTrade.status !== 'Canceled' && e.source === "Attached" &&
                                                    <span
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            setDeleteDocuments(e.origin as Document)
                                                            setShowDeleteModal(true)
                                                        }}
                                                        style={{ cursor: 'pointer', color: 'red' }}
                                                    >
                                                        Delete
                                                    </span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {showDeleteModal && (
                            <DeleteConfirmation
                                onConfirm={() => handleDelete(deleteDocuments)}
                                onCancel={() => setShowDeleteModal(false)}
                            />
                        )}
                        {openPreviewDocument && signableDocument && (
                            <DocumentPreviewModal
                                opened={openPreviewDocument}
                                onClose={() => {
                                    activeSessionRef.current = null;
                                    setOpenPreviewDocument(false);
                                    setCurrentDocumentType('');
                                    setSignableDocument(null);
                                }}
                                documentUrl={signableDocument.url ?? ''}
                                previewHtml={signableDocument.previewHtml ?? null}
                                filename={signableDocument.fileName ?? "Unknown Document.pdf"}
                                documentType={currentDocumentType}
                                subTrade={refinedSubTrade}
                                onAction={handleSignDocument}
                                possibleActions={possibleActions}
                                hasUnappliedTransactions={hasUnappliedTransactions}
                                onRedirectToSubTrade={redirectToSubTrade}
                            />
                        )}
                        {showUnappliedWarning && (
                            <UnappliedTransactionWarningModal
                                onConfirm={handleWarningConfirm}
                                onCancel={handleWarningCancel}
                                onRedirectToSubTrade={redirectToSubTrade}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default DocumentTradePanel;